/**
 * Página de Variantes — ListView + useListViews + useStandardListFilters.
 * Misma estructura y diseño que Categorías/Etiquetas/Modificadores; solo cambian datos y config.
 * Los valores de cada variante se definen en cada producto al asignar la variante.
 */

import { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  BlockStack,
  Banner,
  ChoiceList,
  Modal,
  Text,
  TextField,
  IndexFiltersMode,
  useSetIndexFiltersMode,
} from '@/lib/polaris';
import { supabase } from '@/lib/supabase';
import { fetchAllPaged } from '@/lib/productsApi';
import { useAuth, usePagination } from '@smarty/shared';
import { useAdminToast } from '@/contexts/AdminToastContext';
import { useUnsavedChanges } from '@/contexts/UnsavedChangesContext';
import { storePath } from '@/config/routes.config';
import { PageBreadcrumbTitle } from '@/components/polaris/PageBreadcrumbTitle';
import { ListView, buildListViewPagination } from '@/components/admin/ListView';
import { getVariantsListConfig } from '@/config/listViews';
import { getStatsPageCache, setStatsPageCache, listViewCacheKey } from '@/lib/statsPageCache';
import type { VariantListItem } from '@/config/listViews';
import { filterBySearchName, filterByWithWithoutProducts, sortByNameOrProducts } from '@/lib/listViewFilters';
import { useListViews } from '@/hooks/useListViews';
import { useCanManage } from '@/hooks/useCanManage';
import { useStandardListFilters } from '@/hooks/useStandardListFilters';
import { exportListToExcel, getExportFilename } from '@/lib/exportToExcel';
import { ProductIcon } from '@/lib/polaris-icons';
import { devLog } from '@/lib/utils';

export interface VariantTemplateValue {
  id?: string;
  value: string;
}

export interface VariantTemplate {
  id: string;
  name: string;
  values: VariantTemplateValue[];
  product_count?: number;
}

function toListItem(t: VariantTemplate): VariantListItem {
  return { id: t.id, name: t.name, product_count: t.product_count };
}

const LIST_ID = 'variants';

const VARIANTS_SYSTEM_VIEWS = [
  {
    id: 'todos',
    label: 'Todos',
    isSystem: true,
    query: '',
    filterValues: { products: [] as string[] },
  },
];

const SORT_OPTIONS = [
  { value: 'name_asc', label: 'Nombre A-Z' },
  { value: 'name_desc', label: 'Nombre Z-A' },
  { value: 'products_desc', label: 'Más productos primero' },
  { value: 'products_asc', label: 'Menos productos primero' },
] as const;

const SORT_OPTIONS_POLARIS = SORT_OPTIONS.map((o) => ({
  label: o.label,
  value: o.value.replace('_', ' ') as `${string} ${'asc' | 'desc'}`,
  directionLabel: o.label,
}));

export default function VariantsPage() {
  const { storeSlug } = useParams<{ storeSlug?: string }>();
  const navigate = useNavigate();
  const { tenantId } = useAuth();
  const canManage = useCanManage();
  const toast = useAdminToast();
  const { setUnsaved } = useUnsavedChanges();

  const [templates, setTemplates] = useState<VariantTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<VariantTemplate | null>(null);
  const [newVariantName, setNewVariantName] = useState('');
  const [editVariantName, setEditVariantName] = useState('');
  const [saving, setSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [variantToDelete, setVariantToDelete] = useState<VariantTemplate | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [searchValue, setSearchValue] = useState('');
  const [filterProducts, setFilterProducts] = useState<string[]>([]);
  const [sortOption, setSortOption] = useState<string>(SORT_OPTIONS[0].value);
  const { mode, setMode } = useSetIndexFiltersMode(IndexFiltersMode.Default);

  const filtersRecord = useMemo(
    () => ({ products: filterProducts }),
    [filterProducts]
  );
  const setFiltersFromView = useCallback((f: Record<string, string[]>) => {
    setFilterProducts(f.products ?? []);
  }, []);

  const views = useListViews({
    listId: LIST_ID,
    tenantId: tenantId ?? undefined,
    systemViews: VARIANTS_SYSTEM_VIEWS,
    searchValue,
    filters: filtersRecord,
    setSearchValue,
    setFilters: setFiltersFromView,
    setMode,
    mode,
  });
  const { activeView, handleSwitchView } = views;

  const toUrl = useCallback(
    (path: string) => navigate(storeSlug ? storePath(storeSlug, path) : path),
    [navigate, storeSlug]
  );
  const config = useMemo(
    () => getVariantsListConfig(toUrl, { onAddVariant: () => setDialogOpen(true) }),
    [toUrl]
  );

  const appliedFilters = useMemo(() => {
    const items: { key: string; label: string; onRemove: () => void }[] = [];
    if (filterProducts.length > 0) {
      const labels = filterProducts
        .map((v) => (v === 'with_products' ? 'Con productos' : 'Sin productos'))
        .join(', ');
      items.push({
        key: 'products',
        label: `Usado en productos: ${labels}`,
        onRemove: () => setFilterProducts([]),
      });
    }
    return items;
  }, [filterProducts]);

  const indexFiltersFilters = useMemo(
    () => [
      {
        key: 'products',
        label: 'Usado en productos',
        shortcut: true,
        pinned: true,
        filter: (
          <ChoiceList
            title="Usado en productos"
            titleHidden
            choices={[
              { label: 'Con productos', value: 'with_products' },
              { label: 'Sin productos', value: 'without_products' },
            ]}
            selected={filterProducts}
            onChange={setFilterProducts}
            allowMultiple
          />
        ),
      },
    ],
    [filterProducts]
  );

  const clearFilters = useCallback(() => {
    handleSwitchView(0);
  }, [handleSwitchView]);

  const {
    filterControl,
    columnOrder: tableColumnOrder,
    hiddenColumnKeys: tableHiddenKeys,
    editColumnsMode,
    editingColumnPrefs,
    onColumnOrderChange,
    onToggleColumnVisibility,
  } = useStandardListFilters({
    listId: LIST_ID,
    tenantId: tenantId ?? undefined,
    config,
    views,
    searchValue,
    setSearchValue,
    queryPlaceholder: activeView ? `Buscando en ${activeView.label}` : 'Buscar variantes',
    filters: indexFiltersFilters,
    appliedFilters,
    onClearAll: clearFilters,
    sortOptions: SORT_OPTIONS_POLARIS,
    sortSelected: [sortOption.replace('_', ' ')],
    onSort: (v) => setSortOption((v[0] ?? sortOption).replace(' ', '_')),
    mode,
    setMode,
  });

  const loadTemplates = useCallback(async (revalidateOnly = false) => {
    if (!tenantId) return;
    const cacheKey = listViewCacheKey('variants', tenantId);
    if (!revalidateOnly) {
      const cached = getStatsPageCache<{ items: VariantTemplate[] }>(cacheKey);
      if (cached?.items !== undefined) {
        setTemplates(cached.items);
        setLoading(false);
        loadTemplates(true).catch(() => {});
        return;
      }
    }
    if (!revalidateOnly) setLoading(true);
    try {
      const { data: templatesData, error: errTemplates } = await supabase
        .from('variant_templates')
        .select('id, name')
        .eq('tenant_id', tenantId)
        .order('name');

      if (errTemplates) throw errTemplates;

      // Paginado: antes truncaban en 1000 (PostgREST) → faltaban valores y los
      // product_count por plantilla sub-contaban, en silencio.
      const valuesData = await fetchAllPaged<{ id: string; variant_template_id: string; value: string; sort_order: number }>(
        (from, to) =>
          supabase
            .from('variant_template_values')
            .select('id, variant_template_id, value, sort_order')
            .order('sort_order')
            .order('id') // desempate estable entre páginas
            .range(from, to)
      );

      const countsData = await fetchAllPaged<{ variant_template_id: string }>((from, to) =>
        supabase
          .from('product_variants')
          .select('variant_template_id')
          .not('variant_template_id', 'is', null)
          .order('id')
          .range(from, to)
      );

      const countByTemplate: Record<string, number> = {};
      countsData.forEach((row: { variant_template_id: string }) => {
        const id = row.variant_template_id;
        countByTemplate[id] = (countByTemplate[id] || 0) + 1;
      });

      const valuesByTemplate: Record<string, VariantTemplateValue[]> = {};
      valuesData.forEach((row: { id: string; variant_template_id: string; value: string }) => {
        const tid = row.variant_template_id;
        if (!valuesByTemplate[tid]) valuesByTemplate[tid] = [];
        valuesByTemplate[tid].push({ id: row.id, value: row.value });
      });

      const list: VariantTemplate[] = (templatesData || []).map((t: { id: string; name: string }) => ({
        id: t.id,
        name: t.name,
        values: valuesByTemplate[t.id] || [],
        product_count: countByTemplate[t.id] ?? 0,
      }));

      setTemplates(list);
      setStatsPageCache(cacheKey, { items: list });
    } catch (error: unknown) {
      toast.error('Error al cargar variantes', error instanceof Error ? error.message : 'Error desconocido');
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [tenantId, toast]);

  useLayoutEffect(() => {
    if (tenantId) loadTemplates();
  }, [tenantId, loadTemplates]);

  const listItems = useMemo(() => templates.map(toListItem), [templates]);
  const filteredVariants = useMemo(() => {
    const bySearch = filterBySearchName(listItems, searchValue);
    const byProducts = filterByWithWithoutProducts(bySearch, filterProducts);
    return sortByNameOrProducts(byProducts, sortOption);
  }, [listItems, searchValue, filterProducts, sortOption]);

  const pagination = usePagination();
  useEffect(() => {
    pagination.setTotal(filteredVariants.length);
    if (filteredVariants.length > 0 && pagination.offset >= filteredVariants.length) {
      pagination.goToFirstPage();
    }
  }, [filteredVariants.length, pagination]);

  const paginatedVariants = useMemo(
    () => filteredVariants.slice(pagination.offset, pagination.offset + pagination.pageSize),
    [filteredVariants, pagination.offset, pagination.pageSize]
  );

  const paginationOption = buildListViewPagination(pagination);

  const handleCreate = useCallback(async () => {
    if (!tenantId || !newVariantName.trim()) {
      toast.error('El nombre de la variante es requerido');
      return;
    }
    const nameNorm = newVariantName.trim().toLowerCase();
    if (templates.some((t) => t.name.trim().toLowerCase() === nameNorm)) {
      toast.error('Ya existe una variante con ese nombre. Usa un nombre distinto.');
      return;
    }
    setSaving(true);
    try {
      const { error: errT } = await supabase
        .from('variant_templates')
        .insert({ tenant_id: tenantId, name: newVariantName.trim() });
      if (errT) throw errT;
      toast.success('Variante creada');
      setDialogOpen(false);
      setNewVariantName('');
      await loadTemplates();
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Error al crear variante');
    } finally {
      setSaving(false);
    }
  }, [tenantId, newVariantName, templates, loadTemplates, toast]);

  const openEdit = useCallback((item: VariantListItem) => {
    const t = templates.find((x) => x.id === item.id);
    if (t) {
      setEditingTemplate(t);
      setEditVariantName(t.name);
      setEditDialogOpen(true);
    }
  }, [templates]);

  const handleUpdate = useCallback(async () => {
    if (!editingTemplate) return;
    if (!editVariantName.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    const nameNorm = editVariantName.trim().toLowerCase();
    if (templates.some((t) => t.id !== editingTemplate.id && t.name.trim().toLowerCase() === nameNorm)) {
      toast.error('Ya existe otra variante con ese nombre. Usa un nombre distinto.');
      return;
    }
    setSaving(true);
    try {
      await supabase
        .from('variant_templates')
        .update({ name: editVariantName.trim() })
        .eq('id', editingTemplate.id);
      await supabase
        .from('product_variants')
        .update({ name: editVariantName.trim() })
        .eq('variant_template_id', editingTemplate.id);
      toast.success('Variante actualizada');
      setEditDialogOpen(false);
      setEditingTemplate(null);
      await loadTemplates();
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Error al actualizar variante');
    } finally {
      setSaving(false);
    }
  }, [editingTemplate, editVariantName, templates, loadTemplates, toast]);

  const handleDeleteClick = useCallback((item: VariantListItem) => {
    const t = templates.find((x) => x.id === item.id);
    if (t) {
      setVariantToDelete(t);
      setDeleteModalOpen(true);
    }
  }, [templates]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!variantToDelete) return;
    setDeleting(true);
    try {
      const { error } = await supabase.from('variant_templates').delete().eq('id', variantToDelete.id);
      if (error) throw error;
      toast.success('Variante eliminada');
      setDeleteModalOpen(false);
      setVariantToDelete(null);
      await loadTemplates();
    } catch (error: unknown) {
      devLog.error('Error eliminando variante', error);
      toast.error('Error al eliminar variante');
    } finally {
      setDeleting(false);
    }
  }, [variantToDelete, loadTemplates, toast]);

  const handleBulkAction = useCallback(
    (actionKey: string, ids: string[]) => {
      if (actionKey === 'export' && ids.length > 0) {
        const items = filteredVariants.filter((v) => ids.includes(v.id));
        exportListToExcel({
          columns: config.columns,
          items: items.map((i) => ({ ...i })),
          filename: getExportFilename(config.title),
        });
        toast.success('Exportado', `${items.length} variante(s) exportada(s) correctamente.`);
      }
    },
    [filteredVariants, config, toast]
  );

  // Borrado masivo: la plantilla pone la acción destructiva, el modal y el toast; acá solo el borrado real.
  const bulkDeleteVariants = useCallback(async (ids: string[]) => {
    if (!tenantId) return 0;
    const { error } = await supabase.from('variant_templates').delete().in('id', ids).eq('tenant_id', tenantId);
    if (error) throw error;
    await loadTemplates();
    return ids.length;
  }, [tenantId, loadTemplates]);

  const createModalHasChanges = dialogOpen && newVariantName.trim() !== '';
  const handleDiscardCreate = useCallback(() => {
    setDialogOpen(false);
    setNewVariantName('');
    toast.info('Cambios descartados', 'Se han descartado todos los cambios');
  }, [toast]);

  const handleCreateRef = useRef(handleCreate);
  const handleDiscardCreateRef = useRef(handleDiscardCreate);
  handleCreateRef.current = handleCreate;
  handleDiscardCreateRef.current = handleDiscardCreate;

  useEffect(() => {
    if (!createModalHasChanges) {
      setUnsaved(null);
      return () => setUnsaved(null);
    }
    setUnsaved({
      onSave: () => handleCreateRef.current(),
      onDiscard: () => handleDiscardCreateRef.current(),
    });
    return () => setUnsaved(null);
  }, [createModalHasChanges, setUnsaved]);

  const pageTitleNode = (
    <PageBreadcrumbTitle
      icon={ProductIcon}
      title={config.title}
      onBack={config.onBack}
      backAccessibilityLabel={config.backAccessibilityLabel ?? 'Volver a Productos'}
    />
  );

  return (
    <>
      <ListView<VariantListItem>
        config={config}
        items={paginatedVariants}
        loading={loading}
        pagination={paginationOption}
        loadingMessage="Cargando variantes…"
        pageTitle={pageTitleNode}
        contentBefore={filterControl}
        contentBeforeInsideCard
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        bulkDelete={{ onDelete: bulkDeleteVariants, can: canManage }}
        columnOrder={tableColumnOrder}
        hiddenColumnKeys={tableHiddenKeys}
        editColumnsMode={editColumnsMode}
        columnOrderForEdit={editingColumnPrefs?.columnOrder?.length ? editingColumnPrefs.columnOrder : tableColumnOrder}
        onColumnOrderChange={onColumnOrderChange}
        onToggleColumnVisibility={onToggleColumnVisibility}
        onRowEdit={openEdit}
        onRowDelete={handleDeleteClick}
        onBulkAction={handleBulkAction}
      />

      <Modal
        open={dialogOpen}
        onClose={() => {
          if (!saving) handleDiscardCreate();
        }}
        title="Nueva variante"
        primaryAction={{
          content: saving ? 'Creando…' : 'Crear',
          onAction: handleCreate,
          disabled:
            saving ||
            !newVariantName.trim() ||
            templates.some((t) => t.name.trim().toLowerCase() === newVariantName.trim().toLowerCase()),
          loading: saving,
        }}
        secondaryActions={[
          {
            content: 'Cancelar',
            onAction: handleDiscardCreate,
            disabled: saving,
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <TextField
              label="Nombre de la variante"
              value={newVariantName}
              onChange={setNewVariantName}
              placeholder="ej: Color, Talla, Tamaño…"
              autoComplete="off"
              helpText="Los valores se definen en cada producto al asignar esta variante."
              error={
                newVariantName.trim() !== '' &&
                templates.some((t) => t.name.trim().toLowerCase() === newVariantName.trim().toLowerCase())
                  ? 'Ya existe una variante con ese nombre'
                  : undefined
              }
            />
          </BlockStack>
        </Modal.Section>
      </Modal>

      <Modal
        open={editDialogOpen}
        onClose={() => {
          if (!saving) {
            setEditDialogOpen(false);
            setEditingTemplate(null);
          }
        }}
        title="Editar variante"
        primaryAction={{
          content: saving ? 'Guardando…' : 'Guardar',
          onAction: handleUpdate,
          disabled:
            saving ||
            !editVariantName.trim() ||
            (editingTemplate !== null &&
              templates.some(
                (t) =>
                  t.id !== editingTemplate.id &&
                  t.name.trim().toLowerCase() === editVariantName.trim().toLowerCase()
              )),
          loading: saving,
        }}
        secondaryActions={[
          {
            content: 'Cancelar',
            onAction: () => {
              setEditDialogOpen(false);
              setEditingTemplate(null);
            },
            disabled: saving,
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <TextField
              label="Nombre de la variante"
              value={editVariantName}
              onChange={setEditVariantName}
              placeholder="ej: Color, Talla…"
              autoComplete="off"
              error={
                editVariantName.trim() !== '' &&
                editingTemplate !== null &&
                templates.some(
                  (t) =>
                    t.id !== editingTemplate.id &&
                    t.name.trim().toLowerCase() === editVariantName.trim().toLowerCase()
                )
                  ? 'Ya existe otra variante con ese nombre'
                  : undefined
              }
            />
          </BlockStack>
        </Modal.Section>
      </Modal>

      <Modal
        open={deleteModalOpen}
        onClose={() => {
          if (!deleting) {
            setDeleteModalOpen(false);
            setVariantToDelete(null);
          }
        }}
        title="Eliminar variante"
        primaryAction={{
          content: deleting ? 'Eliminando…' : 'Eliminar',
          tone: 'critical',
          onAction: handleDeleteConfirm,
          loading: deleting,
        }}
        secondaryActions={[
          {
            content: 'Cancelar',
            onAction: () => {
              setDeleteModalOpen(false);
              setVariantToDelete(null);
            },
            disabled: deleting,
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <Banner tone="critical">
              <Text as="p">
                ¿Eliminar la variante "{variantToDelete?.name}"? Se quitará de todos los productos que la usen.
              </Text>
            </Banner>
          </BlockStack>
        </Modal.Section>
      </Modal>

    </>
  );
}
